/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ApolloError } from "@apollo/client";
import { ParentSize } from "@visx/responsive";
import {
  BrickLoader,
  UniversalWrap,
  StackBarChart,
  mapStackBarChartColors,
  ChartType,
  CostAnalysisFields,
  Granularity,
  EstatePeriod,
  DateRange,
} from "~/components";
import { EstateTotals } from "./EstateTotals";
import { getExtraCostDetails, reorderGroups } from "./helpers";
import { useEstateRecordsCostPerApplication } from "./hooks";
import { ChartJobData, EstateGroupBy } from "./types";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: EstatePeriod;
  chartData: ChartJobData | undefined;
  loading: boolean;
  range: DateRange | undefined;
  isCostLoading: boolean;
  error?: ApolloError;
  monthlySavings?: string | null;
};

export function EstateRecordsCostPerApplication({
  startDate,
  endDate,
  granularity,
  period,
  chartData,
  loading,
  error,
  range,
  isCostLoading,
  monthlySavings,
}: Props) {
  const { costPerApplicationChartData, applications } =
    useEstateRecordsCostPerApplication({ chartData, granularity });

  const reoderedApplications = reorderGroups(
    applications,
    CostAnalysisFields.OTHER_APPLICATIONS_COST
  );

  const chartKeys = useMemo(
    () => [...reoderedApplications, CostAnalysisFields.SAVINGS_PLANS],
    [reoderedApplications]
  );

  const stackBarColors = useMemo(
    () => mapStackBarChartColors(chartKeys),
    [chartKeys]
  );

  const extraDetails = getExtraCostDetails(
    chartData?.chartJob?.data?.extraDetails ?? []
  );

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <>
            <StackBarChart
              showOthers
              width={parent.width}
              height={280}
              data={costPerApplicationChartData}
              granularity={granularity}
              loading={loading}
              startDate={startDate}
              endDate={endDate}
              period={period}
              colors={stackBarColors}
              chartType={ChartType.COST_PER_APPLICATION}
              keys={chartKeys}
            />
            <EstateTotals
              period={period}
              range={range}
              isCostLoading={isCostLoading}
              /* Total count for Top10 should come from ChartJobTable  */
              count={0}
              monthlySavings={monthlySavings}
              chartType={EstateGroupBy.APP}
              extraDetails={extraDetails}
            />
          </>
        )}
      </ParentSize>
    </UniversalWrap>
  );
}
